import { createAction } from '@reduxjs/toolkit';
import type { ContactKind } from '@shared/constants/contact-status';
import type { ProfileExitInput } from '@shared/schemas';

/**
 * "Signal" actions: intents the UI dispatches for the saga to act on. Kept out of
 * the slice's reducers: they trigger side effects (load / enrich), not state
 * transitions (the saga owns the resulting `load*` / `enrich*` ones).
 */

/** Load the profile for the given id (dispatched on mount and for silent refresh). */
export const loadRequested = createAction<string>('profileDetail/loadRequested');

/** Start enrichment; `force` bypasses the freshness guard. */
export const enrichRequested = createAction<{ id: string; force: boolean }>(
  'profileDetail/enrichRequested',
);

/** Load the Network tab rows (dispatched on every entry into that tab). */
export const commonNetworkRequested = createAction<string>('profileDetail/commonNetworkRequested');

/** Deactivate this profile globally (admin CTA in the page header). */
export const deactivateRequested = createAction<string>('profileDetail/deactivateRequested');

/** Reactivate this deactivated profile (admin action in the deactivation banner, #659). */
export const reactivateRequested = createAction<string>('profileDetail/reactivateRequested');

/**
 * An admin marks one of the profile's contacts verified, or withdraws the claim
 * (#556). The contact value is never part of this; the endpoint states
 * something about it, it does not edit it.
 */
export const contactStatusRequested = createAction<{
  id: string;
  contact: ContactKind;
  verified: boolean;
}>('profileDetail/contactStatusRequested');

/**
 * An admin sets or removes "Verified by Invyo Expert" from the identity band
 * (#754). Removing it is confirmed by the page before this is dispatched.
 */
export const expertValidationRequested = createAction<{ id: string; validated: boolean }>(
  'profileDetail/expertValidationRequested',
);

/** Load the lists an admin can add this profile to (when the picker opens). */
export const assignableListsRequested = createAction('profileDetail/assignableListsRequested');

/** Admin only: load each type's main category, for the type chip (#629). */
export const typeCategoriesRequested = createAction('profileDetail/typeCategoriesRequested');

/** Admin only: open the "Types" dialog on the profile and load its types (#652). */
export const typesEditRequested = createAction<{ id: string }>('profileDetail/typesEditRequested');

/**
 * One type edit from the "Types" dialog. `reloadProfile: false` when the dialog is
 * not on the profile page (the drawer, #701): the page's profile is not on screen,
 * the drawer redraws its own chips from the answer.
 */
export interface TypeEditPayload {
  id: string;
  type: string;
  reloadProfile?: boolean;
}

/** Take a type off the profile; rules no longer put it back on it. */
export const typeRemoveRequested = createAction<TypeEditPayload>('profileDetail/typeRemoveRequested');

/** Put a type removed by hand back on the profile and let rules tag it again. */
export const typeRestoreRequested = createAction<TypeEditPayload>('profileDetail/typeRestoreRequested');

/** Add a type by hand; a bulk removal from the Profile types page leaves it (#701). */
export const typeAddRequested = createAction<TypeEditPayload>('profileDetail/typeAddRequested');

/** Clients only: open the "Types" dialog for the caller's licence and load its types (#723). */
export const licenceTypesEditRequested = createAction<{ id: string }>('profileDetail/licenceTypesEditRequested');

/** Add a type to the profile for the caller's licence only (#723). */
export const licenceTypeAddRequested = createAction<TypeEditPayload>('profileDetail/licenceTypeAddRequested');

/** Remove a type the caller's licence added (#723). */
export const licenceTypeRemoveRequested = createAction<TypeEditPayload>('profileDetail/licenceTypeRemoveRequested');

/** Load the caller's licence's notes on the profile (dispatched when the Notes tab mounts, #676). */
export const notesLoadRequested = createAction<string>('profileDetail/notesLoadRequested');

/** Add a note of the caller's licence on the profile (#743). */
export const noteAddRequested = createAction<{ id: string; content: string }>('profileDetail/noteAddRequested');

/** Edit one of the caller's notes on the profile (#743). */
export const noteUpdateRequested = createAction<{ id: string; noteId: string; content: string }>(
  'profileDetail/noteUpdateRequested',
);

/** Delete one of the caller's notes on the profile (#743). */
export const noteDeleteRequested = createAction<{ id: string; noteId: string }>('profileDetail/noteDeleteRequested');

/** An admin adds an exit to the profile by hand (#755). */
export const exitAddRequested = createAction<{ id: string; exit: ProfileExitInput }>('profileDetail/exitAddRequested');

/** An admin corrects an exit of the profile, imported or not (#755). */
export const exitUpdateRequested = createAction<{ id: string; exitId: string; exit: ProfileExitInput }>(
  'profileDetail/exitUpdateRequested',
);

/** An admin deletes an exit of the profile, imported or not (#755). */
export const exitDeleteRequested = createAction<{ id: string; exitId: string }>('profileDetail/exitDeleteRequested');

/** An admin adds the profile to one list. `listName` only feeds the success snackbar. */
export const addToListRequested = createAction<{
  profileId: string;
  listId: string;
  listName: string;
}>('profileDetail/addToListRequested');
